// TerrainLayer - Base class for terrain visualization layers (Aspect, Slope)
// Encapsulates common logic: tile management, web workers, caching, mapbox integration
// Subclasses implement processImage() and related specific logic

import { debounce } from '../../utils/terrainLayerUtils';

/**
 * Base class for terrain layers using Mapbox Image Source.
 * Handles:
 * - Web Worker pool management
 * - Tile caching and fetching with fallback
 * - Mapbox integration (addSource, addLayer, removeSource, removeLayer)
 * - Bilinear interpolation for child tile extraction
 * - Coordinate transformations
 */
class TerrainLayer {
    /**
     * Initialize terrain layer
     * @param {string} accessToken - Mapbox access token
     * @param {mapboxgl.Map} map - Mapbox map instance
     * @param {Object} config - Configuration object with layerNamePrefix, etc.
     */
    constructor (accessToken, map, config = {}) {
        this.accessToken = accessToken;
        this.map = map;
        this.layerNamePrefix = config.layerNamePrefix || 'terrain';
        this.isActive = false;
        this.tileCache = new Map();
        this.pendingTiles = new Set();
        this.pendingRequests = new Map(); // Map of tile keys to AbortController
        this.activeLayers = new Set();
        this.currentZoom = null;
        this.maxConcurrentTiles = 16;
        this.lastZoomChangeTime = 0;
        this.tilesLoadingFromZoom = new Set();
        // Create a debounced version of the onMapRender method for this instance
        this.debouncedOnMapRender = debounce(() => this.onMapRender(), 500);
        this.workers = [];
        this.workerQueue = [];
        this.processingTiles = new Map();
        this.initWorkerPool();
    }

    /**
     * Initialize pool of Web Workers
     * @param {number} maxWorkers - Maximum workers (default 4)
     */
    initWorkerPool (maxWorkers = 4) {
        const workerCount = Math.min(navigator.hardwareConcurrency || 2, maxWorkers);
        for (let i = 0; i < workerCount; i++) {
            const worker = new Worker(this.getWorkerPath());
            worker.isAvailable = true;
            this.workers.push(worker);
        }
    }

    /**
     * Get worker script path - override in subclass
     * @returns {string} Path to worker script
     */
    getWorkerPath () {
        throw new Error('getWorkerPath() must be implemented in subclass');
    }

    /**
     * Get available worker from pool
     */
    getAvailableWorker () {
        return this.workers.find(w => w.isAvailable) || null;
    }

    /**
     * Process image data using available worker
     * @param {ImageData} imageData - Image data to process
     * @param {number} z - Zoom level
     * @param {number} x - Tile X coordinate
     * @param {number} y - Tile Y coordinate
     * @returns {Promise<ImageData>} Processed image data
     */
    processImageWithWorker (imageData, z, x, y) {
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line no-var
            var handleMessage, handleError;

            handleMessage = event => {
                const worker = event.target;
                worker.removeEventListener('message', handleMessage);
                worker.removeEventListener('error', handleError);
                worker.isAvailable = true;

                if (event.data.error) {
                    reject(new Error(event.data.error));
                } else {
                    resolve(event.data.imageData);
                }

                if (this.workerQueue.length > 0) {
                    const nextTask = this.workerQueue.shift();
                    nextTask();
                }
            };
            handleError = error => {
                const worker = error.target;
                worker.removeEventListener('message', handleMessage);
                worker.removeEventListener('error', handleError);
                worker.isAvailable = true;
                reject(error);

                if (this.workerQueue.length > 0) {
                    const nextTask = this.workerQueue.shift();
                    nextTask();
                }
            };

            const processWithWorker = () => {
                const worker = this.getAvailableWorker();
                if (!worker) {
                    this.workerQueue.push(processWithWorker);
                    return;
                }

                worker.isAvailable = false;
                worker.addEventListener('message', handleMessage);
                worker.addEventListener('error', handleError);
                this.postMessageToWorker(worker, imageData, z, x, y);
            };

            processWithWorker();
        });
    }

    /**
     * Post message to worker - override in subclass if needed custom data
     * @param {Worker} worker - Worker instance
     * @param {ImageData} imageData - Image data
     * @param {number} z - Zoom level
     * @param {number} x - Tile X
     * @param {number} y - Tile Y
     */
    postMessageToWorker (worker, imageData, z, x, y) {
        worker.postMessage({ imageData, z, x, y });
    }

    /**
     * Add layer to map
     */
    addToMap () {
        if (this.isActive) return;
        this.isActive = true;
        this.map.on('render', this.debouncedOnMapRender);
        // Hook for subclass-specific optimization
        this.optimizeForTerrain();
    }

    /**
     * Remove layer from map
     */
    removeFromMap () {
        if (!this.isActive) return;
        this.isActive = false;
        this.map.off('render', this.debouncedOnMapRender);

        // Abort all pending requests
        this.pendingRequests.forEach(controller => controller.abort());
        this.pendingRequests.clear();

        // Remove all tiles from map
        Array.from(this.activeLayers).forEach(tileKey => {
            this.removeTile(tileKey);
        });

        this.activeLayers.clear();
        this.tileCache.clear();
        this.pendingTiles.clear();
        this.processingTiles.clear();
    }

    /**
     * Handle map render event
     */
    onMapRender () {
        if (!this.isActive) {
            return;
        }

        const bounds = this.map.getBounds();
        const zoom = Math.floor(this.map.getZoom());

        if (zoom !== this.currentZoom) {
            this.currentZoom = zoom;
            this.lastZoomChangeTime = Date.now();
        }

        const visibleTiles = this.getVisibleTiles(bounds, zoom);

        // Load missing tiles
        visibleTiles.forEach(({ z, x, y }) => {
            const tileKey = `${z}-${x}-${y}`;
            if (!this.activeLayers.has(tileKey) && !this.pendingTiles.has(tileKey)) {
                this.loadAndAddTile(z, x, y);
            }
        });

        // Remove tiles that are no longer visible (with debounce)
        const timeSinceZoomChange = Date.now() - this.lastZoomChangeTime;
        if (timeSinceZoomChange > 500) {
            const visibleTileKeys = new Set(visibleTiles.map(t => `${t.z}-${t.x}-${t.y}`));
            const tilesToRemove = Array.from(this.activeLayers).filter(key => !visibleTileKeys.has(key));
            tilesToRemove.forEach(tileKey => this.removeTile(tileKey));
        }

        // Cleanup old tiles if memory pressure is high
        this.performMemoryCleanup(visibleTiles, zoom);
    }

    /**
     * Get visible tiles based on bounds and zoom
     */
    getVisibleTiles (bounds, zoom) {
        const tiles = [];
        const z = Math.max(7, Math.min(zoom, 13)); // Clamp zoom to 7-13

        if (!bounds) {
            return tiles;
        }

        let nwPoint;
        let sePoint;
        try {
            // Try to get corners using Mapbox API methods
            let nw;
            let se;

            if (typeof bounds.getNorthWest === 'function') {
                nw = bounds.getNorthWest();
                se = bounds.getSouthEast();
            } else {
                // eslint-disable-next-line no-underscore-dangle
                nw = bounds._ne;
                // eslint-disable-next-line no-underscore-dangle
                se = bounds._sw;
                if (!nw || !se) {
                    return tiles;
                }
            }

            nwPoint = this.lngLatToTile(nw, z);
            sePoint = this.lngLatToTile(se, z);
        } catch (error) {
            return tiles;
        }

        if (Number.isNaN(nwPoint.x) || Number.isNaN(nwPoint.y) || Number.isNaN(sePoint.x) || Number.isNaN(sePoint.y)) {
            return tiles;
        }

        for (let x = nwPoint.x; x <= sePoint.x; x++) {
            for (let y = nwPoint.y; y <= sePoint.y; y++) {
                const tile = { z, x, y };
                if (!tiles.some(t => t.z === tile.z && t.x === tile.x && t.y === tile.y)) {
                    tiles.push(tile);
                }
            }
        }

        return tiles;
    }

    /**
     * Convert lng/lat to tile coordinates
     */
    lngLatToTile (lngLat, z) {
        if (!lngLat) {
            return { x: 0, y: 0 };
        }

        // Try to extract lng and lat - Mapbox LngLat objects have these properties
        let lng;
        let lat;

        // Direct property access (works for {lng, lat} objects)
        if (lngLat.lng !== null && typeof lngLat.lng === 'number') {
            lng = lngLat.lng;
        } else if (lngLat.lon !== null && typeof lngLat.lon === 'number') {
            lng = lngLat.lon;
        } else if (Array.isArray(lngLat) && typeof lngLat[0] === 'number') {
            lng = lngLat[0];
        } else {
            return { x: 0, y: 0 };
        }

        if (lngLat.lat !== null && typeof lngLat.lat === 'number') {
            lat = lngLat.lat;
        } else if (Array.isArray(lngLat) && typeof lngLat[1] === 'number') {
            lat = lngLat[1];
        } else {
            return { x: 0, y: 0 };
        }

        // Calculate tile coordinates
        const n = Math.pow(2, z);
        const x = Math.floor(((lng + 180) / 360) * n);
        const latRad = (lat * Math.PI) / 180;
        const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
        return { x: Math.max(0, Math.min(x, n - 1)), y: Math.max(0, Math.min(y, n - 1)) };
    }

    /**
     * Load and add tile to map
     */
    loadAndAddTile (z, x, y) {
        const tileKey = `${z}-${x}-${y}`;
        this.pendingTiles.add(tileKey);

        const controller = new AbortController();
        this.pendingRequests.set(tileKey, controller);

        this.fetchTileWithFallback(z, x, y, controller.signal)
            .then(canvas => {
                if (!this.isActive || controller.signal.aborted) {
                    return;
                }

                const url = canvas.toDataURL('image/png');
                const { nwLng, nwLat, neLng, neLat, seLng, seLat, swLng, swLat } = this.getTileCoordinates(z, x, y);

                if (this.map.getSource(`${this.layerNamePrefix}-source-${tileKey}`)) {
                    return;
                }

                this.map.addSource(`${this.layerNamePrefix}-source-${tileKey}`, {
                    type: 'image',
                    url,
                    coordinates: [ [ nwLng, nwLat ], [ neLng, neLat ], [ seLng, seLat ], [ swLng, swLat ] ],
                });

                // Get the first raster/fill/line/symbol layer to add terrain layer before it
                // This ensures terrain layers stay below all vector layers
                const style = this.map.getStyle();
                const layers = style.layers || [];
                const firstVectorLayerId = layers.find(l => l.type !== 'background' && l.id !== `${this.layerNamePrefix}-layer-${tileKey}` && !l.id.startsWith(`${this.layerNamePrefix}-layer-`))?.id;

                this.map.addLayer({
                    id: `${this.layerNamePrefix}-layer-${tileKey}`,
                    type: 'raster',
                    source: `${this.layerNamePrefix}-source-${tileKey}`,
                    paint: { 'raster-opacity': 1.0 },
                }, firstVectorLayerId);

                this.activeLayers.add(tileKey);
                this.pendingTiles.delete(tileKey);
                this.pendingRequests.delete(tileKey);
            })
            .catch(error => {
                if (error.name !== 'AbortError') {
                    // Silent error handling - tile loading failed
                }
                this.pendingTiles.delete(tileKey);
                this.pendingRequests.delete(tileKey);
            });
    }

    /**
     * Fetch tile with fallback to parent zoom levels
     */
    async fetchTileWithFallback (z, x, y, signal) {
        const key = `${z}-${x}-${y}`;
        const cached = this.tileCache.get(key);
        if (cached) {
            return cached;
        }

        // Try current zoom first
        try {
            const response = await fetch(
                `https://tiles.mapterhorn.com/${z}/${x}/${y}.webp`,
                { signal }
            );
            if (response.ok) {
                const blob = await response.blob();
                const canvas = await this.processTile(blob, z, x, y);
                this.tileCache.set(key, canvas);
                return canvas;
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                // Parent tile fetch failed, trying next parent level
            }
        }

        // Try parent zoom levels
        for (let parentZ = z - 1; parentZ >= 0; parentZ--) {
            const parentX = Math.floor(x / Math.pow(2, z - parentZ));
            const parentY = Math.floor(y / Math.pow(2, z - parentZ));
            const parentKey = `${parentZ}-${parentX}-${parentY}`;

            let parentCanvas = this.tileCache.get(parentKey);
            if (!parentCanvas) {
                try {
                    const parentUrl = `https://tiles.mapterhorn.com/${parentZ}/${parentX}/${parentY}.webp`;
                    const parentResponse = await fetch(parentUrl, { signal });
                    if (!parentResponse.ok) {
                        continue;
                    }

                    const blob = await parentResponse.blob();
                    parentCanvas = await this.processTile(blob, parentZ, parentX, parentY);
                    this.tileCache.set(parentKey, parentCanvas);
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        // Parent tile fetch failed, trying next parent level
                    }
                    continue;
                }
            }

            const childCanvas = this.extractChild(parentCanvas, z, x, y, parentZ, parentX, parentY);
            this.tileCache.set(key, childCanvas);
            return childCanvas;
        }

        throw new Error(`Failed to load tile ${key}`);
    }

    /**
     * Process tile blob - override in subclass
     * @param {Blob} blob - Tile blob
     * @param {number} z - Zoom level
     * @param {number} x - Tile X
     * @param {number} y - Tile Y
     * @returns {Promise<Canvas>} Processed canvas
     */
    async processTile (blob, z, x, y) {
        throw new Error('processTile() must be implemented in subclass');
    }

    /**
     * Extract child tile region from processed parent tile
     */
    extractChild (parentCanvas, childZ, childX, childY, parentZ, parentX, parentY) {
        const tileSize = 512;
        const scale = Math.pow(2, childZ - parentZ);
        const tempCtx = parentCanvas.getContext('2d', { willReadFrequently: true });
        const parentData = tempCtx.getImageData(0, 0, tileSize, tileSize);
        const parentPixels = parentData.data;
        const parentTileSize = tileSize / scale;
        const startPixelX = (childX - parentX * scale) * parentTileSize;
        const startPixelY = (childY - parentY * scale) * parentTileSize;
        const canvas = document.createElement('canvas');
        canvas.width = tileSize;
        canvas.height = tileSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const outputData = ctx.createImageData(tileSize, tileSize);
        const outputPixels = outputData.data;

        for (let outY = 0; outY < tileSize; outY++) {
            for (let outX = 0; outX < tileSize; outX++) {
                const srcX = startPixelX + (outX / scale);
                const srcY = startPixelY + (outY / scale);
                const color = this.bilinearInterpolateColor(parentPixels, tileSize, srcX, srcY);
                const outIdx = (outY * tileSize + outX) * 4;
                outputPixels[outIdx] = color.r;
                outputPixels[outIdx + 1] = color.g;
                outputPixels[outIdx + 2] = color.b;
                outputPixels[outIdx + 3] = color.a;
            }
        }

        ctx.putImageData(outputData, 0, 0);
        return canvas;
    }

    /**
     * Bilinear interpolation for color sampling
     */
    bilinearInterpolateColor (pixels, tileSize, x, y) {
        const x0 = Math.floor(x);
        const x1 = Math.min(x0 + 1, tileSize - 1);
        const y0 = Math.floor(y);
        const y1 = Math.min(y0 + 1, tileSize - 1);
        const fx = x - x0;
        const fy = y - y0;
        const idx00 = (y0 * tileSize + x0) * 4;
        const idx10 = (y0 * tileSize + x1) * 4;
        const idx01 = (y1 * tileSize + x0) * 4;
        const idx11 = (y1 * tileSize + x1) * 4;
        const interp = (v00, v10, v01, v11) => {
            return (1 - fx) * (1 - fy) * v00 +
                   fx * (1 - fy) * v10 +
                   (1 - fx) * fy * v01 +
                   fx * fy * v11;
        };
        return {
            r: Math.round(interp(pixels[idx00], pixels[idx10], pixels[idx01], pixels[idx11])),
            g: Math.round(interp(pixels[idx00 + 1], pixels[idx10 + 1], pixels[idx01 + 1], pixels[idx11 + 1])),
            b: Math.round(interp(pixels[idx00 + 2], pixels[idx10 + 2], pixels[idx01 + 2], pixels[idx11 + 2])),
            a: Math.round(interp(pixels[idx00 + 3], pixels[idx10 + 3], pixels[idx01 + 3], pixels[idx11 + 3])),
        };
    }

    /**
     * Get tile geographic coordinates
     */
    getTileCoordinates (z, x, y) {
        const n = Math.pow(2, z);
        const nwLng = (x / n) * 360 - 180;
        const seLng = ((x + 1) / n) * 360 - 180;
        const nwLat = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * (180 / Math.PI);
        const seLat = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 1)) / n))) * (180 / Math.PI);

        return {
            nwLng,
            nwLat,
            neLng: seLng,
            neLat: nwLat,
            seLng,
            seLat,
            swLng: nwLng,
            swLat: seLat,
        };
    }

    /**
     * Load image from URL with CORS
     */
    loadImage (url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Image load failed'));
            img.onabort = () => reject(new Error('Image load aborted'));
            img.src = url;
        });
    }

    /**
     * Process image with worker and fallback to main thread
     * @param {Image} img - Image to process
     * @param {number} z - Zoom level
     * @param {number} x - Tile X
     * @param {number} y - Tile Y
     * @returns {Promise<Canvas>} Processed canvas
     */
    async processImage (img, z, x, y) {
        const tileSize = 512;
        const canvas = document.createElement('canvas');
        canvas.width = tileSize;
        canvas.height = tileSize;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, tileSize, tileSize);

        try {
            const processedImageData = await this.processImageWithWorker(imageData, z, x, y);
            const outputCanvas = document.createElement('canvas');
            outputCanvas.width = tileSize;
            outputCanvas.height = tileSize;
            const outputCtx = outputCanvas.getContext('2d', { willReadFrequently: true });
            const outputImgData = outputCtx.createImageData(tileSize, tileSize);
            outputImgData.data.set(processedImageData.data || processedImageData);
            outputCtx.putImageData(outputImgData, 0, 0);

            return outputCanvas;
        } catch (error) {
            // Worker processing failed, falling back to main thread
            return this.processImageMainThread(img, z, x, y);
        }
    }

    /**
     * Process image on main thread (fallback for worker failure)
     * Override in subclass to implement specific processing logic
     * @param {Image} img - Image to process
     * @param {number} z - Zoom level
     * @param {number} x - Tile X
     * @param {number} y - Tile Y
     * @returns {Canvas} Processed canvas
     */
    processImageMainThread (img, z, x, y) {
        // Default: return unprocessed canvas
        // Subclasses should override this to implement fallback processing
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        return canvas;
    }

    /**
     * Optimize layer for terrain (hook for subclass-specific optimizations)
     * Override in subclass if needed (e.g., add more workers for 3D terrain)
     */
    optimizeForTerrain () {
        // Default: no optimization
        // Subclasses can override this
    }

    /**
     * Cleanup old tiles when memory pressure is high
     */
    performMemoryCleanup (visibleTiles, zoom) {
        // Only cleanup if we have accumulated too many layers (>100)
        if (this.activeLayers.size > 100) {
            this.hardClearOldTiles(visibleTiles);
        }
    }

    /**
     * Hard cleanup of old tiles - only when memory pressure is high
     */
    hardClearOldTiles (visibleTiles) {
        const visibleKeys = new Set(visibleTiles.map(t => `${t.z}-${t.x}-${t.y}`));
        const toRemove = [];

        for (const key of this.activeLayers) {
            if (!visibleKeys.has(key)) {
                toRemove.push(key);
            }
        }

        // Remove half of the off-screen tiles
        const removeCount = Math.floor(toRemove.length / 2);
        for (let i = 0; i < removeCount; i++) {
            const key = toRemove[i];
            this.removeTile(key);
        }
    }

    /**
     * Remove tile from map
     */
    removeTile (tileKey) {
        try {
            const layerId = `${this.layerNamePrefix}-layer-${tileKey}`;
            const sourceId = `${this.layerNamePrefix}-source-${tileKey}`;

            if (this.map.getLayer(layerId)) {
                this.map.removeLayer(layerId);
            }
            if (this.map.getSource(sourceId)) {
                this.map.removeSource(sourceId);
            }
        } catch (error) {
            // Error removing tile - safe to ignore
        }
        this.activeLayers.delete(tileKey);
    }
}

export default TerrainLayer;
